import Config from '../_config/config';
import myAxios from '../_services/axiositeachWrapper';

export interface CounselCorpusDocMeta {
  Id: number;
  DocKey: string;
  Title: string;
  DocType: 'call_summary' | 'transcript' | 'playbook' | 'framework' | 'other';
  CallType: string | null;
  Segment: string | null;
  StudentLabel: string | null;
  GradeLabel: string | null;
  Topics: string | null;
  TokenEstimate: number | null;
  Status: 'active' | 'inactive';
  UpdatedAt: string | null;
}

export interface CounselCorpusDoc extends CounselCorpusDocMeta {
  Content: string;
  SourceFile: string | null;
}

export interface CounselGlossaryEntry {
  Id: number;
  EntryType: 'correction' | 'warning' | 'team';
  Term: string;
  Meaning: string;
  Active: number;
}

export interface CounselConversation {
  Id: number;
  Title: string;
  CreatedAt: string;
  UpdatedAt: string;
  MessageCount: number;
}

export interface CounselMessage {
  Id: number;
  Role: 'user' | 'assistant';
  Content: string;
  Citations: string | null;
  Model: string | null;
  OutputTokens: number | null;
  CreatedAt: string;
  FeedbackRating: number | null;
}

export interface ChatStreamResult {
  fullText: string;
  conversationId: number;
  messageId: number;
  citations: string[];
  model: string;
  usage?: {
    input_tokens: number;
    output_tokens: number;
    cache_read_input_tokens: number;
    cache_creation_input_tokens: number;
  };
}

class CounselService {
  private readonly baseUrl = `${Config.nodeSiteUrl}v1/counsel`;

  // ---- knowledge core ----
  async getCorpus(): Promise<CounselCorpusDocMeta[]> {
    const res = await myAxios.get<any>(`${this.baseUrl}/corpus`);
    return res.data?.docs ?? [];
  }

  async getCorpusDoc(docKey: string): Promise<CounselCorpusDoc | null> {
    const res = await myAxios.get<any>(`${this.baseUrl}/corpus/${encodeURIComponent(docKey)}`);
    return res.data?.doc ?? null;
  }

  async createCorpusDoc(payload: {
    title: string;
    content: string;
    docType: string;
    docKey?: string;
    callType?: string;
    segment?: string;
    studentLabel?: string;
    gradeLabel?: string;
  }): Promise<string> {
    const res = await myAxios.post<any>(`${this.baseUrl}/corpus`, payload);
    return res.data?.docKey;
  }

  async updateCorpusDoc(docKey: string, patch: Record<string, unknown>): Promise<void> {
    await myAxios.put(`${this.baseUrl}/corpus/${encodeURIComponent(docKey)}`, patch);
  }

  async getGlossary(): Promise<CounselGlossaryEntry[]> {
    const res = await myAxios.get<any>(`${this.baseUrl}/glossary`);
    return res.data?.entries ?? [];
  }

  async addGlossaryEntry(entryType: string, term: string, meaning: string): Promise<void> {
    await myAxios.post(`${this.baseUrl}/glossary`, { entryType, term, meaning });
  }

  async updateGlossaryEntry(id: number, patch: Record<string, unknown>): Promise<void> {
    await myAxios.put(`${this.baseUrl}/glossary/${id}`, patch);
  }

  // ---- conversations ----
  async getConversations(): Promise<CounselConversation[]> {
    const res = await myAxios.get<any>(`${this.baseUrl}/conversations`);
    return res.data?.conversations ?? [];
  }

  async getMessages(conversationId: number): Promise<CounselMessage[]> {
    const res = await myAxios.get<any>(`${this.baseUrl}/conversations/${conversationId}/messages`);
    return res.data?.messages ?? [];
  }

  async archiveConversation(conversationId: number): Promise<void> {
    await myAxios.delete(`${this.baseUrl}/conversations/${conversationId}`);
  }

  async sendFeedback(messageId: number, rating: 1 | -1, note?: string): Promise<void> {
    await myAxios.post(`${this.baseUrl}/messages/${messageId}/feedback`, { rating, note });
  }

  // ---- streaming chat (fetch — myAxios/XHR cannot stream) ----
  async streamChat(
    body: { message: string; conversationId?: number },
    onChunk: (delta: string) => void,
  ): Promise<ChatStreamResult> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser && currentUser !== 'undefined') {
      const token = JSON.parse(currentUser).token;
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000);
    let fullText = '';
    let result: Partial<ChatStreamResult> = {};

    try {
      const response = await fetch(`${this.baseUrl}/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!response.ok) {
        let message = `Request failed (${response.status})`;
        try {
          const err = await response.json();
          message = err?.error?.message || message;
        } catch {
          /* keep default */
        }
        throw new Error(message);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('Streaming not supported by this browser');
      const decoder = new TextDecoder();
      let buffer = '';

      const processLine = (line: string) => {
        if (!line.startsWith('data: ')) return;
        const data = JSON.parse(line.slice(6));
        if (data.error) throw new Error(data.error);
        if (data.chunk) {
          fullText += data.chunk;
          onChunk(data.chunk);
        }
        if (data.done) {
          result = {
            conversationId: data.conversationId,
            messageId: data.messageId,
            citations: data.citations || [],
            model: data.model,
            usage: data.usage,
          };
        }
      };

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          const trimmed = line.trim();
          if (trimmed) processLine(trimmed);
        }
      }
      if (buffer.trim()) processLine(buffer.trim());
    } finally {
      clearTimeout(timeoutId);
    }

    if (!result.conversationId) throw new Error('The stream ended unexpectedly');
    return { fullText, ...(result as ChatStreamResult) };
  }
}

const counselService = new CounselService();
export default counselService;
